import {
  RtcMessageAreaStatus,
  RtcMessageConfigurationEvent,
  RtcMessageEvent,
  RtcMessagePgm,
  RtcMessageSystemStatusReload,
  RtcMessageUserAccessControl,
  RtcMessageUserSystem,
  RtcMessageVersionUpdate,
  RtcService,
} from './rtc.service';
import * as socketIo from 'socket.io-client';
import { Injectable, Injector, OnDestroy } from '@angular/core';
import { autoinject, useDebouncedFunction } from 'src/shim';
import { RegionService } from '../region.service';
import { UserService } from '../user.service';
import { Observable, Subject, Subscription } from 'rxjs';
import { AuthService } from '../auth.service';
import { LoggerService } from '../logger.service';
import { SystemsService } from 'src/app/services/systems.service';
import { SystemService } from '../system/system.service';
import { CurrentUserData } from 'src/api/v3/common';
import { LocatorService } from 'src/app/services/locator.service';

@Injectable()
export class WebRtcService implements RtcService, OnDestroy {
  private socket: any;

  private get region() { return autoinject(LocatorService.injector, RegionService); }
  private get user() { return autoinject(LocatorService.injector, UserService); }
  private get auth() { return autoinject(LocatorService.injector, AuthService); }
  private get l() { return autoinject(LocatorService.injector, LoggerService); }
  private lastIntrests: number[] = [];
  private lastUserId: number = null;
  constructor(private injector: Injector) {
    setTimeout(
      () =>
        (this.accountChangeSubscription = this.auth.onAccountOrRegionChnage.subscribe(() => {
          this.reinitializeSafe();
        })),
      1500
    );
    const [reinitializeSafe, _, cancelReinitialize] = useDebouncedFunction(() => this.reInitialize(), 2000);
    this.reinitializeSafe = reinitializeSafe;
    this.cancelReinitialize = cancelReinitialize;
  }

  private reinitializeSafe: () => Promise<void>;
  private cancelReinitialize: () => void;

  ngOnDestroy(): void {
    this.close();
    this.accountChangeSubscription.unsubscribe();
  }

  private systemStatusChangeSubject = new Subject<RtcMessageSystemStatusReload>();
  private versionUpdateSubject = new Subject<RtcMessageVersionUpdate>();
  private eventsSubject = new Subject<RtcMessageEvent>();
  private areaStatusSubject = new Subject<RtcMessageAreaStatus>();
  private pgmSubject = new Subject<RtcMessagePgm>();
  private userSystemSubject = new Subject<RtcMessageUserSystem>();
  private configurationEventSubject = new Subject<RtcMessageConfigurationEvent>();
  private userAccessControlSubject = new Subject<RtcMessageUserAccessControl>();

  public systemStatusChange: Observable<RtcMessageSystemStatusReload> = this.systemStatusChangeSubject.asObservable();
  public versionUpdate: Observable<RtcMessageVersionUpdate> = this.versionUpdateSubject.asObservable();
  public events: Observable<RtcMessageEvent> = this.eventsSubject.asObservable();
  public areaStatus: Observable<RtcMessageAreaStatus> = this.areaStatusSubject.asObservable();
  public pgm: Observable<RtcMessagePgm> = this.pgmSubject.asObservable();
  public userSystem: Observable<RtcMessageUserSystem> = this.userSystemSubject.asObservable();
  public configurationEvent: Observable<RtcMessageConfigurationEvent> = this.configurationEventSubject.asObservable();
  public userAccessControl: Observable<RtcMessageUserAccessControl> = this.userAccessControlSubject.asObservable();

  private accountChangeSubscription: Subscription;

  public close() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.l.log('Uždarytas socket', 'WebRtcService');
    }
  }

  public updateIntrests(systemIds: number[]): void {
    this.lastIntrests = systemIds;
    if (!this.socket) {
      this.reinitializeSafe();
      return;
    }
    this.socket.emit('client-options', {
      version: 2,
      systems: systemIds,
      user: this.lastUserId,
    });
  }

  public connect(): void {
    if (this.socket) { return; }
    const oldSystems = this.injector.get(SystemsService).systems.map((s) => s.id);
    const newSystems = this.injector.get(SystemService).systems.keys();
    this.lastIntrests = [...new Set([...oldSystems, ...newSystems]).values()];
    this.reinitializeSafe();
  }

  private async reInitialize(): Promise<void> {
    this.close();
    if (!this.auth.hasToken()) {
      this.l.log('Vartotojas neprisijungę, nabandome prisijungti.', 'WebRtcService');
      return;
    }
    let user: CurrentUserData = this.user.currentUser;
    if (!user?.socket_token) {
      this.l.log('Vartotojas neturi socket tokeno, bandome gauti tokeną ir tada bandysime vėl..', 'WebRtcService');
      user = await this.auth.loadUserData(false);
    }
    this.l.log('Prisijungiama prie socketo...', 'WebRtcService');
    this.socket = socketIo(this.region.ActiveRegion.backEndHost + ':' + user.socket_port, {
      multiplex: false,
      forceNew: false,
      query: 'token=' + user.socket_token,
    });
    this.lastUserId = user.id;
    this.socket.on('connect', () => {
      this.l.log('Prisijungta prie socketo.', 'WebRtcService');
      this.updateIntrests(this.lastIntrests);
    });
    this.socket.on('system-status-reload', (msg) => {
      this.systemStatusChangeSubject.next(JSON.parse(msg));
    });
    this.socket.on('version-update', (msg) => {
      this.versionUpdateSubject.next(JSON.parse(msg));
    });
    this.socket.on('events-channel', (msg) => {
      this.eventsSubject.next(JSON.parse(msg));
    });
    this.socket.on('auto-reload-channel', (msg) => {
      this.areaStatusSubject.next(JSON.parse(msg));
    });
    this.socket.on('pgm-channel', (msg) => {
      this.pgmSubject.next(JSON.parse(msg));
    });
    this.socket.on('user-system:' + user.id, (msg) => {
      this.userSystemSubject.next(JSON.parse(msg));
    });
    this.socket.on('configuration-event:' + user.id, (msg) => {
      this.configurationEventSubject.next(JSON.parse(msg));
    });
  }
}
